import React, { useEffect, useRef } from 'react';
import './CommitmentPage.css';
import SEO from '../SEO';

// Reusable Intersection Observer Hook for animations (no changes)
const useAnimateOnScroll = (options) => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('royal-is-visible');
        observer.unobserve(entry.target);
      }
    }, options);
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, options]);
  return ref;
};

export default function CommitmentRoyal() {
  const heroRef = useAnimateOnScroll({ threshold: 0.2 });
  const article1Ref = useAnimateOnScroll({ threshold: 0.2 });
  const article2Ref = useAnimateOnScroll({ threshold: 0.2 });
  const article3Ref = useAnimateOnScroll({ threshold: 0.2 });
  const quoteRef = useAnimateOnScroll({ threshold: 0.8 });
  const closingRef = useAnimateOnScroll({ threshold: 0.5 });

  return (
    <>
    <SEO 
  title="Sustainability & Eco-Friendly Design Commitments"
  description="We prioritize the planet. Learn about our commitment to using FSC-certified reclaimed wood, low-VOC finishes, and zero-waste manufacturing in our Jodhpur factory."
  keywords="sustainable furniture manufacturer, fsc certified wood furniture, eco friendly handicrafts, green manufacturing india, zero waste furniture, low voc wood finish"
  url="/commitments"
/>
   
    <div className="commitment-royal-page">
      {/* --- UPDATED HERO SECTION --- */}
      <section ref={heroRef} className="commitment-royal-hero">
        
        {/* Giant background text for architectural feel */}
        <div className="commitment-royal-hero-bg-text" aria-hidden="true">
          SOHANI
        </div>
        
        {/* Main hero content layered on top */}
        <div className="commitment-royal-hero-content">
          <h1 className="commitment-royal-hero-title">The Sohani Handicrafts Covenant</h1>
          <p className="commitment-royal-hero-subtitle">Our Word, Forged in Wood and Iron.</p>
        </div>
      </section>
      {/* --- END OF UPDATED SECTION --- */}


      <div className="commitment-royal-content">
        <article ref={article1Ref} className="commitment-royal-article">
          <h2 className="commitment-royal-article-title">
            <span className="commitment-royal-roman-numeral">I.</span>
            Our Craft
          </h2>
          <p>
            <span className="commitment-royal-dropcap">O</span>ur workshop is a sanctuary of artistry, a place where tradition informs every modern design. We produce a comprehensive range of antique reproduction furniture, from statement beds and dining tables to the finest details of consoles and kitchen shelves. Each piece is born from an exquisite fusion of solid wood and hand-wrought iron. Yet, our truest passion is realized in custom creation. We believe furniture is deeply personal, an extension of you. Therefore, you are the designer; you choose the dimensions, the form, the finish. We are simply the hands that bring your unique vision to life.
          </p>
        </article>

        <article ref={article2Ref} className="commitment-royal-article">
          <h2 className="commitment-royal-article-title">
            <span className="commitment-royal-roman-numeral">II.</span>
            Our Quality
          </h2>
          <p>
            <span className="commitment-royal-dropcap">Q</span>uality is the soul of our work; it is the silent promise embedded in every joint and surface. A dedicated team of quality controllers follows the journey of each piece, from the first cut to the final polish. Our furniture is engineered for maximum comfort and built for unwavering durability. We hold one principle as sacred: every item is constructed from solid hardwood. There are no exceptions. No particle board, no laminates, no fillers. We offer you nothing less than the pure, uncompromised integrity of wood, finished to protect its beauty for generations.
          </p>
        </article>
        
        <div ref={quoteRef} className="commitment-royal-pull-quote">
          <p>"Our best salespeople are our customers. Our reputation for service and individual attention is something we work hard every day to maintain."</p>
        </div>

        <article ref={article3Ref} className="commitment-royal-article">
          <h2 className="commitment-royal-article-title">
            <span className="commitment-royal-roman-numeral">III.</span>
            Our Promise
          </h2>
          <p>
            <span className="commitment-royal-dropcap">A</span>s a third-generation family business, our name is our bond. Our production volume allows us to offer competitive pricing, but we have perfected the craft of delivering exceptional value without ever sacrificing quality. This principle extends to our ethics. We are a proud ethical trading organization: all our timber is sourced from proper legal channels, we enforce a strict no-child-labor policy, and we ensure our artisans are remunerated as the true artists they are. Our goal is not a single transaction, but a lifelong relationship built on trust and mutual satisfaction.
          </p>
        </article>

        <div ref={closingRef} className="commitment-royal-closing-statement">
          <div className="commitment-royal-divider"></div>
          <p>Great Pricing. The Best Product. Professional Help. And the Best Service.</p>
          <span>This is our promise to you.</span>
        </div>
      </div>
    </div>
     </>
  );
}